import { useState, useEffect } from "react";

// Real estate prices come from the Parcl V4 oracle, which publishes daily at
// ~7:50 AM New York time; update_system pushes it on chain at 7:55 AM ET.
// Using the New York wall clock keeps the countdown right across DST
// (11:55 UTC in summer, 12:55 UTC in winter).
const UPDATE_TIME_ZONE = "America/New_York";
const UPDATE_HOUR = 7;
const UPDATE_MINUTE = 55;
const WINDOW_MINUTES = 15;

const nyFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: UPDATE_TIME_ZONE,
  hourCycle: "h23",
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  second: "numeric",
});

// Offset of New York from UTC at `date`, in ms (negative: behind UTC)
const nyOffsetMs = (date) => {
  const p = Object.fromEntries(nyFormatter.formatToParts(date).map(({ type, value }) => [type, Number(value)]));
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(date.getTime() / 1000) * 1000;
};

// The UTC instant of today's (New York date) update time
const updateInstantFor = (date) => {
  const p = Object.fromEntries(nyFormatter.formatToParts(date).map(({ type, value }) => [type, Number(value)]));
  const localAsUtc = Date.UTC(p.year, p.month - 1, p.day, UPDATE_HOUR, UPDATE_MINUTE);
  return localAsUtc - nyOffsetMs(new Date(localAsUtc));
};

/**
 * Countdown to the next daily real estate price update (07:55 New York time).
 * isMarketOpen is true for WINDOW_MINUTES after the update lands.
 * @returns {Object} timeRemaining and isMarketOpen state
 */
const useMarketOpenCountdown = () => {
  const [timeRemaining, setTimeRemaining] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [isMarketOpen, setIsMarketOpen] = useState(false);

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = Date.now();
      const windowMs = WINDOW_MINUTES * 60 * 1000;

      let target = updateInstantFor(new Date(now));
      if (now >= target + windowMs) {
        target = updateInstantFor(new Date(now + 24 * 3600 * 1000));
      }

      const open = now >= target && now < target + windowMs;
      setIsMarketOpen(open);

      const remainingSecs = Math.max(0, Math.floor(((open ? target + windowMs : target) - now) / 1000));
      setTimeRemaining({
        hours: Math.floor(remainingSecs / 3600),
        minutes: Math.floor((remainingSecs % 3600) / 60),
        seconds: remainingSecs % 60,
      });
    };

    // Calculate immediately
    calculateTimeRemaining();

    // Update every second
    const interval = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, []);

  return { timeRemaining, isMarketOpen };
};

export default useMarketOpenCountdown;
